import { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
 Card,
 CardContent,
 CardActions,
 Typography,
 Box,
 Alert,
 Link,
 Tooltip,
} from "@mui/material";
import { useDialog, useCart } from "hooks";
import {
 Carousel,
 LikeChip,
 RatingChip,
 PriceTypography,
 ShareButton,
 TimeTypography,
 CartButton,
} from "components";
import { getStatusWarningMessage } from "utilities";
import { API_ENDPOINTS } from "constants";
import { logAnalyticsEvent } from "services/logAnalyticsEvent";

export const DealCard = memo(
 function DealCard({
  deal,
  onLike,
  onUnlike,
  isLiked,
  currentUser,
  isOwner,
  showTimeTypography,
 }) {
  const navigate = useNavigate();
  const { confirm, openCheckout } = useDialog();
  const [hover, sethover] = useState(false);

  const { getItemCount, addToCart, replaceCart, removeFromCart, cart } =
   useCart();
  const cartCount = getItemCount(deal.id);

  const handleLikeClick = useCallback(
   async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!currentUser) {
     const result = await confirm({
      title: "Sign in required",
      description: "Sign in to add to favourites?",
      confirmLabel: "Sign in",
      cancelLabel: "Cancel",
     });
     if (result) window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
     return;
    }

    if (isLiked) {
     onUnlike(deal.id, currentUser.uid);
    } else {
     onLike(deal.id, currentUser.uid);
    }
   },
   [currentUser, isLiked, onLike, onUnlike, deal.id, confirm],
  );

  const handleCardClick = useCallback(() => {
   logAnalyticsEvent("deal_clicked", {
    deal_id: deal.id,
    deal_name: deal.title,
    price: deal.price,
    restaurant_id: deal.restaurantId,
    restaurant_name: deal.restaurantName,
   });
   navigate(`/restaurant/${deal.restaurantId}/deal/${deal.id}`, {
    replace: false,
    state: { scrollToTop: true },
   });
  }, [navigate, deal.restaurantId, deal.id, deal.title, deal.price, deal.restaurantName]);

  const handleRestaurantClick = useCallback(
   (event) => {
    event.preventDefault();
    event.stopPropagation();
    logAnalyticsEvent("restaurant_clicked", {
     restaurant_id: deal.restaurantId,
     restaurant_name: deal.restaurantName,
    });
    navigate(`/restaurant/${deal.restaurantId}`, {
     replace: false,
     state: { scrollToTop: true },
    });
   },
   [navigate, deal.restaurantId, deal.restaurantName],
  );

  const buildMeta = () => {
   const loc = deal.restaurantMainLocation;
   const address =
    loc?.formattedAddress ||
    [loc?.street, loc?.suburb, loc?.city].filter(Boolean).join(", ") ||
    null;
   return {
    dealMeta: {
     name: deal.title,
     price: deal.price,
     imageUrl: deal.imageUrls?.[0] || null,
    },
    restaurantMeta: {
     name: deal.restaurantName,
     logoUrl: deal.restaurantLogoUrl ?? null,
     rating: deal.restaurantRating ?? null,
     reviewCount: deal.restaurantReviewCount ?? null,
     address,
     serviceStyles: deal.restaurantServiceStyles ?? [],
    },
   };
  };

  const handleAdd = async (event) => {
   event.stopPropagation();

   if (!currentUser) {
    const result = await confirm({
     title: "Sign in required",
     description: "Sign in to add to cart?",
     confirmLabel: "Sign in",
     cancelLabel: "Cancel",
    });
    if (result) window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
    return;
   }

   const { dealMeta, restaurantMeta } = buildMeta();
   const added = addToCart(
    deal.id,
    deal.restaurantId,
    dealMeta,
    restaurantMeta,
   );

   if (!added) {
    // Restaurant conflict — ask user if they want to clear and switch
    const existingName = cart.restaurantMeta?.name || "another restaurant";
    const result = await confirm({
     title: "Replace cart?",
     description: `Your cart has items from ${existingName}. Start a new cart with items from ${deal.restaurantName}?`,
     confirmLabel: "Replace cart",
     cancelLabel: "Keep current",
    });
    if (result) {
     replaceCart(deal.id, deal.restaurantId, dealMeta, restaurantMeta);
    }
   }
  };

  const handleRemove = (e) => {
   e.stopPropagation();
   removeFromCart(deal.id);
  };

  const handleCheckout = (e) => {
   e.stopPropagation();
   openCheckout();
  };

  let statusToDisplay = null;
  let typeToDisplay = "deal";
  let severity = "warning";

  const isPending =
   isOwner &&
   deal.status &&
   ["pendingCreate", "pendingEdit", "pendingDelete"].includes(deal.status);

  if (isPending) {
   statusToDisplay = deal.status;
   typeToDisplay = "deal";
   severity = "warning";
  } else if (deal.restaurantStatus === "disabled") {
   statusToDisplay = deal.restaurantStatus;
   typeToDisplay = "restaurant";
   severity = "error";
  } else if (deal.status === "disabled") {
   statusToDisplay = deal.status;
   typeToDisplay = "deal";
   severity = "error";
  }

  const showStatusWarning = !!statusToDisplay;

  return (
   <Card
    sx={{
     maxWidth: 345,
     height: "fit-content",
     display: "flex",
     flexDirection: "column",
     transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
     "&:hover": { transform: "scale(1.03)", boxShadow: 6 },
     borderRadius: 4,
    }}
    onClick={handleCardClick}
    style={{ cursor: "pointer" }}
   >
    <Box sx={{ position: "relative" }}>
     <Carousel slidesToShow={1} images={deal.imageUrls} />
     {showStatusWarning ? (
      <Box
       sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        color: "white",
       }}
      >
       <Alert severity={severity}>
        {getStatusWarningMessage(statusToDisplay, typeToDisplay)}
       </Alert>
      </Box>
     ) : (
      deal.isSponsored && (
       <Box
        sx={{
         position: "absolute",
         bottom: 0,
         left: 0,
         width: "100%",
         bgcolor: "rgba(0, 0, 0, 0.75)",
         color: "white",
         padding: "10px",
        }}
       >
        <Typography variant="caption" fontWeight="bold">
         Sponsored
        </Typography>
       </Box>
      )
     )}
    </Box>

    <CardContent sx={{ padding: "0px 16px", flexGrow: 1 }}>
     <Typography variant="h6">{deal.title || "Untitled Deal"}</Typography>
     <Link
      underline="hover"
      color="text.secondary"
      onClick={handleRestaurantClick}
     >
      {deal.restaurantName}
     </Link>
     <PriceTypography price={deal.price} originalPrice={deal.originalPrice} />
     {showTimeTypography && (
      <TimeTypography
       start={deal.timeStart}
       end={deal.timeEnd}
       allDay={deal.isAllDay}
       homepage={true}
       daysOfTheWeek={deal.daysOfTheWeek}
      />
     )}
    </CardContent>

    <CardActions sx={{ mt: "auto", pt: 1 }}>
     <Box
      sx={{
       display: "flex",
       justifyContent: "space-between",
       alignItems: "center",
       width: "100%",
      }}
     >
      <RatingChip rating={deal.rating} count={deal.reviewCount} />
      <Box
       sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        minHeight: 40,
       }}
      >
       <Box
        sx={{
         display: "flex",
         alignItems: "center",
         transition: "all 0.3s ease-in-out",
         opacity: hover && cartCount > 0 ? 0 : 1,
         maxWidth: hover && cartCount > 0 ? 0 : "500px",
         transform:
          hover && cartCount > 0 ? "scale(0.9) translateX(-10px)" : "scale(1)",
         pointerEvents: hover && cartCount > 0 ? "none" : "auto",
         overflow: "hidden",
         whiteSpace: "nowrap",
        }}
       >
        <LikeChip
         count={deal.likedBy?.length || 0}
         liked={isLiked}
         onClick={handleLikeClick}
        />
        <ShareButton
         restaurantId={deal.restaurantId}
         dealId={deal.id}
         size="small"
        />
       </Box>

       <Tooltip
        title={isOwner ? "Owners cannot add their own food" : ""}
        disableHoverListener={!isOwner} // Only show if isOwner is true
        arrow
       >
        <span>
         <CartButton
          disabled={isOwner}
          count={cartCount}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onCheckout={handleCheckout}
          hover={hover}
          onHover={sethover}
         />
        </span>
       </Tooltip>
      </Box>
     </Box>
    </CardActions>
   </Card>
  );
 },
 (prevProps, nextProps) => {
  return (
   prevProps.deal.id === nextProps.deal.id &&
   prevProps.deal.likedBy?.length === nextProps.deal.likedBy?.length &&
   prevProps.deal.status === nextProps.deal.status &&
   prevProps.deal.restaurantStatus === nextProps.deal.restaurantStatus &&
   prevProps.isLiked === nextProps.isLiked &&
   prevProps.isOwner === nextProps.isOwner
  );
 },
);
